import { bytesToHex } from "viem";
import { trackTransaction } from "../../../../analytics/track/transaction.js";
import { getCachedChain } from "../../../../chains/utils.js";
import { eth_sendRawTransaction } from "../../../../rpc/actions/eth_sendRawTransaction.js";
import { getRpcClient } from "../../../../rpc/rpc.js";
import { getAddress } from "../../../../utils/address.js";
import { toHex } from "../../../../utils/encoding/hex.js";
import { parseTypedData } from "../../../../utils/signatures/helpers/parse-typed-data.js";
import { getUserStatus } from "../actions/get-enclave-user-status.js";
import { signMessage as signEnclaveMessage } from "../actions/sign-message.enclave.js";
import { signTransaction as signEnclaveTransaction } from "../actions/sign-transaction.enclave.js";
import { signTypedData as signEnclaveTypedData } from "../actions/sign-typed-data.enclave.js";
export class EnclaveWallet {
    constructor({ client, ecosystem, address, storage, }) {
        Object.defineProperty(this, "client", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "ecosystem", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "address", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "localStorage", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.client = client;
        this.ecosystem = ecosystem;
        this.address = address;
        this.localStorage = storage;
    }
    /**
     * Store the auth token for use
     * @returns `{walletAddress: string }` The user's wallet details
     * @internal
     */
    async postWalletSetUp(authResult) {
        await this.localStorage.saveAuthCookie(authResult.storedToken.cookieString);
    }
    /**
     * Gets the current user's details
     * @internal
     */
    async getUserWalletStatus() {
        const token = await this.localStorage.getAuthCookie();
        if (!token) {
            return { status: "Logged Out" };
        }
        const userStatus = await getUserStatus({
            authToken: token,
            client: this.client,
            ecosystem: this.ecosystem,
        });
        if (!userStatus) {
            return { status: "Logged Out" };
        }
        const wallet = userStatus.wallets[0];
        const authDetails = {
            email: userStatus.linkedAccounts.find((account) => account.details.email !== undefined)?.details.email,
            phoneNumber: userStatus.linkedAccounts.find((account) => account.details.phone !== undefined)?.details.phone,
            userWalletId: userStatus.id || "",
            recoveryShareManagement: "ENCLAVE",
        };
        if (!wallet) {
            return {
                status: "Logged In, Wallet Uninitialized",
                authDetails,
            };
        }
        return {
            status: "Logged In, Wallet Initialized",
            walletAddress: wallet.address,
            authDetails,
            account: await this.getAccount(),
        };
    }
    /**
     * Returns an account to perform wallet operations
     * @internal
     */
    async getAccount() {
        const client = this.client;
        const storage = this.localStorage;
        const address = this.address;
        const ecosystem = this.ecosystem;
        const _signTransaction = async (tx) => {
            const rpcRequest = getRpcClient({
                client,
                chain: getCachedChain(tx.chainId),
            });
            const transaction = {
                to: tx.to ? getAddress(tx.to) : undefined,
                data: tx.data,
                value: typeof tx.value === "bigint" ? toHex(tx.value) : undefined,
                gas: typeof tx.gas === "bigint"
                    ? toHex(tx.gas + tx.gas / BigInt(10))
                    : undefined, // Add a 10% buffer to gas
                nonce: typeof tx.nonce === "number"
                    ? toHex(tx.nonce)
                    : toHex(await import("../../../../rpc/actions/eth_getTransactionCount.js").then(({ eth_getTransactionCount }) => eth_getTransactionCount(rpcRequest, {
                        address: this.address,
                        blockTag: "pending",
                    }))),
                chainId: toHex(tx.chainId),
            };
            if (typeof tx.maxFeePerGas === "bigint") {
                transaction.maxFeePerGas = toHex(tx.maxFeePerGas);
                transaction.maxPriorityFeePerGas =
                    typeof tx.maxPriorityFeePerGas === "bigint"
                        ? toHex(tx.maxPriorityFeePerGas)
                        : undefined;
                transaction.type = 2;
            }
            else {
                transaction.gasPrice =
                    typeof tx.gasPrice === "bigint" ? toHex(tx.gasPrice) : undefined;
                transaction.type = 0;
            }
            return signEnclaveTransaction({
                client,
                storage,
                payload: transaction,
            });
        };
        return {
            address: getAddress(address),
            async signTransaction(tx) {
                if (!tx.chainId) {
                    throw new Error("chainId required in tx to sign");
                }
                return _signTransaction({
                    chainId: tx.chainId,
                    ...tx,
                });
            },
            async sendTransaction(tx) {
                const rpcRequest = getRpcClient({
                    client,
                    chain: getCachedChain(tx.chainId),
                });
                const signedTx = await _signTransaction(tx);
                const transactionHash = await eth_sendRawTransaction(rpcRequest, signedTx);
                trackTransaction({
                    client,
                    ecosystem,
                    chainId: tx.chainId,
                    walletAddress: address,
                    walletType: "inApp",
                    transactionHash,
                    contractAddress: tx.to ?? undefined,
                    gasPrice: tx.gasPrice,
                });
                return { transactionHash };
            },
            async signMessage({ message }) {
                const messagePayload = (() => {
                    if (typeof message === "string") {
                        return { message, isRaw: false };
                    }
                    return {
                        message: typeof message.raw === "string"
                            ? message.raw
                            : bytesToHex(message.raw),
                        isRaw: true,
                    };
                })();
                const { signature } = await signEnclaveMessage({
                    client,
                    payload: messagePayload,
                    storage,
                });
                return signature;
            },
            async signTypedData(_typedData) {
                const parsedTypedData = parseTypedData(_typedData);
                const { signature } = await signEnclaveTypedData({
                    client,
                    payload: parsedTypedData,
                    storage,
                });
                return signature;
            },
        };
    }
}
//# sourceMappingURL=enclave-wallet.js.map